import React, { useState, useEffect } from "react";
import { Calculator, Send, ArrowLeft, ArrowRight, FileText, Calendar } from "lucide-react";
import { useSearchParams } from "react-router-dom";

const AdPortalPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [modalType, setModalType] = useState<'none' | 'estimate' | 'proposal' | 'consultation' | 'brand'>('none');
  const [selectedBrand, setSelectedBrand] = useState<any>(null);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    company: "",
    contact: "",
    email: "",
    phone: "",
    adType: "3D Digital Display",
    duration: 1,
    region: "UAE",
    estimatedCost: 0,
    message: "",
    preferredDate: "",
    preferredTime: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const slides = [
    {
      brand: "DHB GROUP",
      title: "Premium OOH Advertising Across GCC & Asia",
      subtitle: "Cross-Border Campaign Excellence",
      description: "From Dubai's skyline to Seoul's districts, we deliver high-impact outdoor advertising that captivates audiences and drives measurable results.",
      cta: "Start Your Campaign",
      ctaSecondary: "View Portfolio",
      bgGradient: "from-blue-900 to-slate-800",
      shapes: [
        { type: "s1", color: "from-red-400 to-orange-400" },
        { type: "s2", color: "from-blue-400 to-purple-500" },
        { type: "s3", color: "from-green-400 to-cyan-400" }
      ]
    },
    {
      brand: "DIGITAL INNOVATION",
      title: "3D Immersive Displays & LED Wraps",
      subtitle: "Next-Generation DOOH Technology",
      description: "Airport terminals, luxury malls, and premium locations featuring cutting-edge 3D displays that create unforgettable brand experiences.",
      cta: "Get Estimate",
      ctaSecondary: "Learn More",
      bgGradient: "from-purple-900 to-indigo-800",
      shapes: [
        { type: "s1", color: "from-purple-400 to-pink-400" },
        { type: "s2", color: "from-cyan-400 to-blue-500" },
        { type: "s3", color: "from-yellow-400 to-orange-400" }
      ]
    },
    {
      brand: "MARKET EXPANSION",
      title: "GCC ↔ Asia Strategic Campaigns",
      subtitle: "Cross-Cultural Brand Positioning",
      description: "Whether you're an Arab company entering Asia or an Asian brand targeting the Gulf, we provide full-spectrum localization and placement.",
      cta: "Plan Campaign",
      ctaSecondary: "Case Studies",
      bgGradient: "from-emerald-900 to-teal-800",
      shapes: [
        { type: "s1", color: "from-emerald-400 to-teal-400" },
        { type: "s2", color: "from-blue-400 to-indigo-500" },
        { type: "s3", color: "from-pink-400 to-rose-400" }
      ]
    }
  ];

  const adOptions = [
    { name: "3D Digital Display", base: 15000, desc: "Immersive 3D visuals at airports, malls, and downtown areas." },
    { name: "Highway Mega Billboard", base: 12000, desc: "100–150m billboard placement across high-traffic highways." },
    { name: "Airport Façade & LED Wrap", base: 18000, desc: "High visibility on airport approach roads and façades." },
    { name: "Intersection Static Board", base: 8000, desc: "High dwell-time exposure at traffic lights and city crossroads." },
    { name: "Integrated Consultancy", base: 10000, desc: "Full campaign strategy linking GCC ↔ Asia markets." },
  ];

  const regionMultiplier: Record<string, number> = {
    UAE: 1.2,
    Saudi: 1.1,
    Qatar: 1.05,
    Korea: 1.0,
    Japan: 1.3,
    Singapore: 1.25,
  };

  const brandCases = [
    {
      name: "Samsung",
      category: "Technology & Innovation",
      image: "https://public.readdy.ai/ai/img_res/6a56a877bcbedf1225cb8687930ba5e7.jpg",
      campaign: {
        title: "Galaxy S24 Ultra Launch - Dubai Mall 3D Display",
        location: "Dubai Mall, UAE",
        duration: "3 months",
        format: "3D Anamorphic LED Display",
        results: {
          brandAwareness: "+340%",
          salesIncrease: "+180%",
          engagement: "+250%"
        },
        description: "Samsung's Galaxy S24 Ultra launch featured a stunning 3D anamorphic display at Dubai Mall, creating an immersive experience that showcased the phone's AI capabilities through holographic-style visuals.",
        campaignImage: "https://readdy.ai/api/search-image?query=Futuristic%203D%20holographic%20Samsung%20Galaxy%20smartphone%20display%20at%20luxury%20shopping%20mall%2C%20anamorphic%20LED%20screen%20showing%20floating%20phone%20with%20AI%20features%2C%20crowds%20watching%20immersive%20technology%20demonstration%2C%20premium%20retail%20environment%20with%20modern%20architecture&width=600&height=400&seq=samsung-3d-campaign-2024&orientation=landscape"
      }
    },
    {
      name: "LG",
      category: "Consumer Electronics",
      image: "https://readdy.ai/api/search-image?query=LG%20official%20corporate%20logo%20on%20clean%20white%20background%2C%20professional%20brand%20identity%2C%20high%20resolution%20company%20logo%2C%20minimalist%20design%20with%20clear%20branding%2C%20corporate%20photography%20with%20perfect%20lighting%20and%20sharp%20details&width=200&height=100&seq=lg-logo-2024&orientation=landscape",
      campaign: {
        title: "OLED TV Campaign - Seoul Station Digital Wrap",
        location: "Seoul Station, South Korea",
        duration: "2 months",
        format: "Digital Building Wrap",
        results: {
          brandAwareness: "+280%",
          salesIncrease: "+160%",
          engagement: "+220%"
        },
        description: "LG's OLED TV campaign transformed Seoul Station with a massive digital building wrap, demonstrating perfect black levels and infinite contrast through architectural-scale displays.",
        campaignImage: "https://readdy.ai/api/search-image?query=Massive%20LG%20OLED%20TV%20advertisement%20wrapping%20entire%20Seoul%20train%20station%20building%2C%20digital%20LED%20facade%20showing%20ultra-high%20definition%20TV%20displays%2C%20urban%20commuters%20and%20modern%20Korean%20architecture%2C%20evening%20cityscape%20with%20illuminated%20advertising&width=600&height=400&seq=lg-seoul-campaign-2024&orientation=landscape"
      }
    },
    {
      name: "SoftBank",
      category: "Telecommunications",
      image: "https://readdy.ai/api/search-image?query=SoftBank%20official%20corporate%20logo%20on%20clean%20white%20background%2C%20professional%20brand%20identity%2C%20high%20resolution%20company%20logo%2C%20minimalist%20design%20with%20clear%20branding%2C%20corporate%20photography%20with%20perfect%20lighting%20and%20sharp%20details&width=200&height=100&seq=softbank-logo-2024&orientation=landscape",
      campaign: {
        title: "5G Network Launch - Tokyo Shibuya Crossing",
        location: "Shibuya Crossing, Tokyo",
        duration: "4 months",
        format: "Multi-Screen Synchronized Display",
        results: {
          brandAwareness: "+320%",
          salesIncrease: "+200%",
          engagement: "+290%"
        },
        description: "SoftBank's 5G launch synchronized multiple screens across Shibuya Crossing, creating a unified visual experience that demonstrated ultra-fast connectivity through real-time interactive content.",
        campaignImage: "https://readdy.ai/api/search-image?query=SoftBank%205G%20advertisement%20synchronized%20across%20multiple%20digital%20billboards%20at%20busy%20Shibuya%20crossing%20Tokyo%2C%20futuristic%20network%20visualization%2C%20crowds%20of%20people%20crossing%20street%2C%20neon%20lights%20and%20modern%20Japanese%20urban%20landscape%20at%20night&width=600&height=400&seq=softbank-shibuya-campaign-2024&orientation=landscape"
      }
    },
    {
      name: "Gucci",
      category: "Luxury Fashion",
      image: "https://readdy.ai/api/search-image?query=Gucci%20official%20corporate%20logo%20on%20clean%20white%20background%2C%20professional%20brand%20identity%2C%20high%20resolution%20company%20logo%2C%20minimalist%20design%20with%20clear%20branding%2C%20corporate%20photography%20with%20perfect%20lighting%20and%20sharp%20details&width=200&height=100&seq=gucci-logo-2024&orientation=landscape",
      campaign: {
        title: "Aria Collection - Doha Airport VIP Lounge",
        location: "Hamad International Airport, Qatar",
        duration: "6 months",
        format: "Premium Lounge Display",
        results: {
          brandAwareness: "+260%",
          salesIncrease: "+140%",
          engagement: "+180%"
        },
        description: "Gucci's Aria collection was showcased through elegant displays in Doha's VIP lounges, targeting ultra-high-net-worth travelers with personalized luxury experiences.",
        campaignImage: "https://readdy.ai/api/search-image?query=Elegant%20Gucci%20luxury%20fashion%20display%20in%20premium%20airport%20VIP%20lounge%2C%20sophisticated%20interior%20design%20with%20marble%20and%20gold%20accents%2C%20high-end%20fashion%20showcase%2C%20exclusive%20traveler%20environment%2C%20Qatar%20luxury%20terminal%20atmosphere&width=600&height=400&seq=gucci-doha-campaign-2024&orientation=landscape"
      }
    },
    {
      name: "Polo Ralph Lauren",
      category: "Fashion & Lifestyle",
      image: "https://readdy.ai/api/search-image?query=Polo%20Ralph%20Lauren%20official%20corporate%20logo%20on%20clean%20white%20background%2C%20professional%20brand%20identity%2C%20high%20resolution%20company%20logo%2C%20minimalist%20design%20with%20clear%20branding%2C%20corporate%20photography%20with%20perfect%20lighting%20and%20sharp%20details&width=200&height=100&seq=polo-logo-2024&orientation=landscape",
      campaign: {
        title: "Summer Collection - Marina Bay Sands",
        location: "Marina Bay Sands, Singapore",
        duration: "3 months",
        format: "Luxury Mall Integration",
        results: {
          brandAwareness: "+240%",
          salesIncrease: "+120%",
          engagement: "+160%"
        },
        description: "Polo Ralph Lauren's summer collection campaign integrated seamlessly with Marina Bay Sands' luxury retail environment, creating an aspirational lifestyle narrative for affluent shoppers.",
        campaignImage: "https://readdy.ai/api/search-image?query=Polo%20Ralph%20Lauren%20summer%20fashion%20campaign%20at%20Marina%20Bay%20Sands%20luxury%20shopping%20mall%20Singapore%2C%20elegant%20retail%20display%20with%20premium%20clothing%2C%20sophisticated%20shoppers%2C%20modern%20architecture%20with%20water%20features%20and%20upscale%20ambiance&width=600&height=400&seq=polo-singapore-campaign-2024&orientation=landscape"
      }
    },
    {
      name: "Microsoft",
      category: "Business Solutions",
      image: "https://readdy.ai/api/search-image?query=Microsoft%20official%20corporate%20logo%20on%20clean%20white%20background%2C%20professional%20brand%20identity%2C%20high%20resolution%20company%20logo%2C%20minimalist%20design%20with%20clear%20branding%2C%20corporate%20photography%20with%20perfect%20lighting%20and%20sharp%20details&width=200&height=100&seq=microsoft-logo-2024&orientation=landscape",
      campaign: {
        title: "Azure AI Solutions - DIFC Business District",
        location: "Dubai International Financial Centre",
        duration: "4 months",
        format: "Corporate Building Display",
        results: {
          brandAwareness: "+300%",
          salesIncrease: "+220%",
          engagement: "+270%"
        },
        description: "Microsoft's Azure AI campaign targeted business leaders in Dubai's financial district with sophisticated displays showcasing enterprise solutions and digital transformation capabilities.",
        campaignImage: "https://readdy.ai/api/search-image?query=Microsoft%20Azure%20AI%20business%20advertisement%20on%20modern%20glass%20office%20building%20in%20Dubai%20financial%20district%2C%20professional%20corporate%20environment%2C%20business%20people%20in%20suits%2C%20futuristic%20technology%20visualization%2C%20sleek%20urban%20architecture&width=600&height=400&seq=microsoft-dubai-campaign-2024&orientation=landscape"
      }
    },
    {
      name: "Chanel",
      category: "Luxury Beauty & Fashion",
      image: "https://readdy.ai/api/search-image?query=Chanel%20official%20corporate%20logo%20on%20clean%20white%20background%2C%20professional%20brand%20identity%2C%20high%20resolution%20company%20logo%2C%20minimalist%20design%20with%20clear%20branding%2C%20corporate%20photography%20with%20perfect%20lighting%20and%20sharp%20details&width=200&height=100&seq=chanel-logo-2024&orientation=landscape",
      campaign: {
        title: "No. 5 Centennial - Gangnam District Premium",
        location: "Gangnam District, Seoul",
        duration: "5 months",
        format: "Luxury District Takeover",
        results: {
          brandAwareness: "+380%",
          salesIncrease: "+190%",
          engagement: "+310%"
        },
        description: "Chanel's No. 5 centennial celebration transformed Gangnam's luxury shopping district with elegant displays that honored the iconic fragrance's heritage while appealing to modern Korean luxury consumers.",
        campaignImage: "https://readdy.ai/api/search-image?query=Elegant%20Chanel%20No%205%20perfume%20advertisement%20in%20upscale%20Gangnam%20shopping%20district%20Seoul%2C%20luxury%20fashion%20boutiques%2C%20sophisticated%20Korean%20shoppers%2C%20premium%20retail%20environment%20with%20modern%20architecture%20and%20designer%20storefronts&width=600&height=400&seq=chanel-gangnam-campaign-2024&orientation=landscape"
      }
    },
    {
      name: "L'Oréal",
      category: "Beauty & Cosmetics",
      image: "https://readdy.ai/api/search-image?query=LOreal%20official%20corporate%20logo%20on%20clean%20white%20background%2C%20professional%20brand%20identity%2C%20high%20resolution%20company%20logo%2C%20minimalist%20design%20with%20clear%20branding%2C%20corporate%20photography%20with%20perfect%20lighting%20and%20sharp%20details&width=200&height=100&seq=loreal-logo-2024&orientation=landscape",
      campaign: {
        title: "Paris Beauty Week - Riyadh Fashion District",
        location: "Riyadh Fashion District, Saudi Arabia",
        duration: "3 months",
        format: "Beauty Retail Integration",
        results: {
          brandAwareness: "+290%",
          salesIncrease: "+170%",
          engagement: "+240%"
        },
        description: "L'Oréal's Paris Beauty Week campaign in Riyadh's fashion district showcased inclusive beauty through culturally sensitive displays that resonated with Saudi Arabia's evolving beauty market.",
        campaignImage: "https://readdy.ai/api/search-image?query=LOreal%20Paris%20beauty%20campaign%20in%20modern%20Riyadh%20fashion%20district%2C%20elegant%20cosmetics%20display%20with%20diverse%20models%2C%20luxury%20beauty%20retail%20environment%2C%20contemporary%20Saudi%20Arabian%20shopping%20center%20with%20premium%20beauty%20products&width=600&height=400&seq=loreal-riyadh-campaign-2024&orientation=landscape"
      }
    },
    {
      name: "Adidas",
      category: "Sports & Fashion",
      image: "https://readdy.ai/api/search-image?query=Adidas%20official%20corporate%20logo%20on%20clean%20white%20background%2C%20professional%20brand%20identity%2C%20high%20resolution%20company%20logo%2C%20minimalist%20design%20with%20clear%20branding%2C%20corporate%20photography%20with%20perfect%20lighting%20and%20sharp%20details&width=200&height=100&seq=adidas-logo-2024&orientation=landscape",
      campaign: {
        title: "World Cup Collection - Qatar Sports Hub",
        location: "Qatar Sports Hub, Doha",
        duration: "6 months",
        format: "Sports Complex Integration",
        results: {
          brandAwareness: "+350%",
          salesIncrease: "+210%",
          engagement: "+280%"
        },
        description: "Adidas leveraged Qatar's World Cup legacy with dynamic displays at the Sports Hub, connecting football heritage with cutting-edge athletic performance technology.",
        campaignImage: "https://readdy.ai/api/search-image?query=Adidas%20World%20Cup%20football%20campaign%20at%20Qatar%20Sports%20Hub%2C%20dynamic%20athletic%20displays%20with%20soccer%20players%2C%20modern%20sports%20facility%20architecture%2C%20football%20fans%20and%20athletic%20equipment%2C%20energetic%20sports%20marketing%20environment&width=600&height=400&seq=adidas-qatar-campaign-2024&orientation=landscape"
      }
    },
    {
      name: "Coca-Cola",
      category: "Beverage & Lifestyle",
      image: "https://readdy.ai/api/search-image?query=Coca%20Cola%20official%20corporate%20logo%20on%20clean%20white%20background%2C%20professional%20brand%20identity%2C%20high%20resolution%20company%20logo%2C%20minimalist%20design%20with%20clear%20branding%2C%20corporate%20photography%20with%20perfect%20lighting%20and%20sharp%20details&width=200&height=100&seq=cocacola-logo-2024&orientation=landscape",
      campaign: {
        title: "Share a Coke - Busan Beach Festival",
        location: "Haeundae Beach, Busan",
        duration: "2 months",
        format: "Beach Festival Activation",
        results: {
          brandAwareness: "+270%",
          salesIncrease: "+150%",
          engagement: "+200%"
        },
        description: "Coca-Cola's Share a Coke campaign created an immersive beach experience in Busan, combining personalized bottles with interactive displays that celebrated Korean summer culture.",
        campaignImage: "https://readdy.ai/api/search-image?query=Coca%20Cola%20Share%20a%20Coke%20beach%20festival%20campaign%20at%20Haeundae%20Beach%20Busan%2C%20summer%20lifestyle%20activation%20with%20personalized%20bottles%2C%20beachgoers%20enjoying%20refreshments%2C%20colorful%20festival%20atmosphere%20with%20ocean%20backdrop%20and%20Korean%20beach%20culture&width=600&height=400&seq=cocacola-busan-campaign-2024&orientation=landscape"
      }
    },
    {
      name: "Toyota",
      category: "Automotive Innovation",
      image: "https://readdy.ai/api/search-image?query=Toyota%20official%20corporate%20logo%20on%20clean%20white%20background%2C%20professional%20brand%20identity%2C%20high%20resolution%20company%20logo%2C%20minimalist%20design%20with%20clear%20branding%2C%20corporate%20photography%20with%20perfect%20lighting%20and%20sharp%20details&width=200&height=100&seq=toyota-logo-2024&orientation=landscape",
      campaign: {
        title: "Hybrid Future - Abu Dhabi Corniche",
        location: "Abu Dhabi Corniche, UAE",
        duration: "4 months",
        format: "Waterfront Digital Display",
        results: {
          brandAwareness: "+310%",
          salesIncrease: "+180%",
          engagement: "+250%"
        },
        description: "Toyota's hybrid future campaign along Abu Dhabi's Corniche showcased sustainable mobility solutions through innovative displays that highlighted the brand's environmental commitment.",
        campaignImage: "https://readdy.ai/api/search-image?query=Toyota%20hybrid%20car%20advertisement%20along%20Abu%20Dhabi%20Corniche%20waterfront%2C%20modern%20eco-friendly%20vehicles%20display%2C%20UAE%20skyline%20backdrop%2C%20sustainable%20transportation%20showcase%2C%20luxury%20Middle%20Eastern%20urban%20environment%20with%20palm%20trees&width=600&height=400&seq=toyota-abudhabi-campaign-2024&orientation=landscape"
      }
    },
    {
      name: "Starbucks",
      category: "Food & Beverage",
      image: "https://readdy.ai/api/search-image?query=Starbucks%20official%20corporate%20logo%20on%20clean%20white%20background%2C%20professional%20brand%20identity%2C%20high%20resolution%20company%20logo%2C%20minimalist%20design%20with%20clear%20branding%2C%20corporate%20photography%20with%20perfect%20lighting%20and%20sharp%20details&width=200&height=100&seq=starbucks-logo-2024&orientation=landscape",
      campaign: {
        title: "Reserve Roastery - Myeongdong District",
        location: "Myeongdong, Seoul",
        duration: "3 months",
        format: "Premium Coffee Experience",
        results: {
          brandAwareness: "+260%",
          salesIncrease: "+140%",
          engagement: "+190%"
        },
        description: "Starbucks Reserve Roastery campaign in Seoul's Myeongdong district elevated the coffee experience through artisanal displays that showcased premium coffee culture and craftsmanship.",
        campaignImage: "https://readdy.ai/api/search-image?query=Starbucks%20Reserve%20premium%20coffee%20roastery%20campaign%20in%20Myeongdong%20Seoul%2C%20artisanal%20coffee%20experience%20with%20Korean%20customers%2C%20upscale%20cafe%20interior%2C%20coffee%20roasting%20equipment%2C%20sophisticated%20urban%20coffee%20culture%20atmosphere&width=600&height=400&seq=starbucks-myeongdong-campaign-2024&orientation=landscape"
      }
    },
    {
      name: "Dior",
      category: "Premium Beauty & Fashion",
      image: "https://readdy.ai/api/search-image?query=Dior%20official%20corporate%20logo%20on%20clean%20white%20background%2C%20professional%20brand%20identity%2C%20high%20resolution%20company%20logo%2C%20minimalist%20design%20with%20clear%20branding%2C%20corporate%20photography%20with%20perfect%20lighting%20and%20sharp%20details&width=200&height=100&seq=dior-logo-2024&orientation=landscape",
      campaign: {
        title: "Miss Dior - Kuwait Avenues Mall",
        location: "The Avenues Mall, Kuwait",
        duration: "4 months",
        format: "Luxury Retail Takeover",
        results: {
          brandAwareness: "+330%",
          salesIncrease: "+200%",
          engagement: "+280%"
        },
        description: "Dior's Miss Dior campaign transformed Kuwait's premier shopping destination with sophisticated displays that celebrated feminine elegance and luxury craftsmanship.",
        campaignImage: "https://readdy.ai/api/search-image?query=Dior%20Miss%20Dior%20luxury%20fashion%20campaign%20at%20upscale%20Kuwait%20Avenues%20Mall%2C%20elegant%20perfume%20and%20fashion%20display%2C%20sophisticated%20Middle%20Eastern%20shoppers%2C%20premium%20retail%20environment%20with%20luxury%20boutique%20atmosphere&width=600&height=400&seq=dior-kuwait-campaign-2024&orientation=landscape"
      }
    },
    {
      name: "Mercedes-Benz",
      category: "Luxury Automotive",
      image: "https://readdy.ai/api/search-image?query=Mercedes%20Benz%20official%20corporate%20logo%20on%20clean%20white%20background%2C%20professional%20brand%20identity%2C%20high%20resolution%20company%20logo%2C%20minimalist%20design%20with%20clear%20branding%2C%20corporate%20photography%20with%20perfect%20lighting%20and%20sharp%20details&width=200&height=100&seq=mercedes-logo-2024&orientation=landscape",
      campaign: {
        title: "EQS Electric Luxury - Osaka Bay Area",
        location: "Osaka Bay Area, Japan",
        duration: "5 months",
        format: "Automotive Showcase District",
        results: {
          brandAwareness: "+360%",
          salesIncrease: "+230%",
          engagement: "+320%"
        },
        description: "Mercedes-Benz EQS campaign in Osaka Bay showcased electric luxury through innovative displays that demonstrated the future of sustainable premium mobility.",
        campaignImage: "https://readdy.ai/api/search-image?query=Mercedes-Benz%20EQS%20electric%20luxury%20car%20campaign%20in%20modern%20Osaka%20Bay%20area%2C%20futuristic%20automotive%20display%2C%20Japanese%20urban%20landscape%2C%20premium%20electric%20vehicle%20showcase%2C%20sophisticated%20technology%20demonstration%20with%20city%20skyline&width=600&height=400&seq=mercedes-osaka-campaign-2024&orientation=landscape"
      }
    },
    {
      name: "Nike",
      category: "Sports & Lifestyle",
      image: "https://readdy.ai/api/search-image?query=Nike%20official%20corporate%20logo%20on%20clean%20white%20background%2C%20professional%20brand%20identity%2C%20high%20resolution%20company%20logo%2C%20minimalist%20design%20with%20clear%20branding%2C%20corporate%20photography%20with%20perfect%20lighting%20and%20sharp%20details&width=200&height=100&seq=nike-logo-2024&orientation=landscape",
      campaign: {
        title: "Just Do It - Jeddah Sports City",
        location: "Jeddah Sports City, Saudi Arabia",
        duration: "3 months",
        format: "Sports Complex Activation",
        results: {
          brandAwareness: "+340%",
          salesIncrease: "+190%",
          engagement: "+270%"
        },
        description: "Nike's Just Do It campaign at Jeddah Sports City inspired athletic excellence through dynamic displays that celebrated Saudi Arabia's growing sports culture and youth empowerment.",
        campaignImage: "https://readdy.ai/api/search-image?query=Nike%20Just%20Do%20It%20sports%20campaign%20at%20Jeddah%20Sports%20City%20Saudi%20Arabia%2C%20dynamic%20athletic%20displays%20with%20diverse%20athletes%2C%20modern%20sports%20facility%2C%20inspirational%20fitness%20motivation%2C%20energetic%20sports%20marketing%20with%20Saudi%20youth%20culture&width=600&height=400&seq=nike-jeddah-campaign-2024&orientation=landscape"
      }
    }
  ];

  const socialPlatforms = [
    {
      name: "Instagram",
      icon: "ri-instagram-line",
      url: "https://instagram.com/dhbgroup",
      alt: "Instagram – DHB Official"
    },
    {
      name: "LinkedIn",
      icon: "ri-linkedin-line",
      url: "https://linkedin.com/company/dhbgroup",
      alt: "LinkedIn – DHB Official"
    },
    {
      name: "Facebook",
      icon: "ri-facebook-line",
      url: "https://facebook.com/dhbgroup",
      alt: "Facebook – DHB Official"
    },
    {
      name: "YouTube",
      icon: "ri-youtube-line",
      url: "https://youtube.com/dhbgroup",
      alt: "YouTube – DHB Official"
    },
    {
      name: "X",
      icon: "ri-twitter-x-line",
      url: "https://x.com/dhbgroup",
      alt: "X – DHB Official"
    },
    {
      name: "TikTok",
      icon: "ri-tiktok-line",
      url: "https://tiktok.com/@dhbgroup",
      alt: "TikTok – DHB Official"
    },
    {
      name: "WhatsApp",
      icon: "ri-whatsapp-line",
      url: "https://wa.me/971501234567",
      alt: "WhatsApp Business – DHB Official"
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 7000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const modalParam = searchParams.get('modal');
    if (modalParam === 'consultation') {
      setModalType('consultation');
    }
  }, [searchParams]);

  const calculateEstimate = () => {
    const ad = adOptions.find((a) => a.name === form.adType);
    const base = ad ? ad.base : 10000;
    const regionFactor = regionMultiplier[form.region] || 1;
    return base * regionFactor * form.duration;
  };

  const handleBrandClick = (brand: any) => {
    setSelectedBrand(brand);
    setModalType('brand');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      if (modalType === 'estimate') {
        setForm({ ...form, estimatedCost: calculateEstimate() });
        setStep(2);
      } else {
        // Actual form submission
        const formData = new FormData();
        formData.append('company', form.company);
        formData.append('contact', form.contact);
        formData.append('email', form.email);
        formData.append('phone', form.phone);
        
        if (modalType === 'consultation') {
          formData.append('preferredDate', form.preferredDate);
          formData.append('preferredTime', form.preferredTime);
          formData.append('message', form.message);
          formData.append('requestType', 'consultation');
          
          const response = await fetch('https://readdy.ai/api/form/d4aodfq34l4q15jhdi9g', {
            method: 'POST',
            body: formData
          });

          if (response.ok) {
            setSubmitted(true);
          } else {
            throw new Error('Form submission failed.');
          }
        } else {
          formData.append('adType', form.adType);
          formData.append('duration', form.duration.toString());
          formData.append('region', form.region);
          formData.append('message', form.message);
          formData.append('requestType', modalType);
          
          const response = await fetch('https://readdy.ai/api/form/d4aodfq34l4q15jhdi90', {
            method: 'POST',
            body: formData
          });

          if (response.ok) {
            setSubmitted(true);
          } else {
            throw new Error('Form submission failed.');
          }
        }
      }
    } catch (error) {
      console.error('Form submission error:', error);
      alert('An error occurred during submission. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirm = async () => {
    setIsSubmitting(true);
    
    try {
      const formData = new FormData();
      formData.append('company', form.company);
      formData.append('contact', form.contact);
      formData.append('email', form.email);
      formData.append('phone', form.phone);
      formData.append('adType', form.adType);
      formData.append('duration', form.duration.toString());
      formData.append('region', form.region);
      formData.append('estimatedCost', form.estimatedCost.toString());
      formData.append('message', form.message);
      formData.append('requestType', 'estimate_consultation');

      const response = await fetch('https://readdy.ai/api/form/d4aodfq34l4q15jhdi90', {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        setSubmitted(true);
      } else {
        throw new Error('Form submission failed.');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      alert('An error occurred during submission. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const openModal = (type: 'estimate' | 'proposal' | 'consultation') => {
    setModalType(type);
    setStep(1);
    setSubmitted(false);
  };

  const closeModal = () => {
    setModalType('none');
    setStep(1);
    setSubmitted(false);
    setSelectedBrand(null);
  };

  const resetForm = () => {
    setForm({
      company: "",
      contact: "",
      email: "",
      phone: "",
      adType: "3D Digital Display",
      duration: 1,
      region: "UAE",
      estimatedCost: 0,
      message: "",
      preferredDate: "",
      preferredTime: "",
    });
  };

  const scrollToPortfolio = () => {
    const portfolioSection = document.getElementById('brand-portfolio-section');
    if (portfolioSection) {
      portfolioSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleExploreGCCMarkets = () => {
    window.REACT_APP_NAVIGATE('/');
    setTimeout(() => {
      const asiaMarketSection = document.getElementById('asia-market-section');
      if (asiaMarketSection) {
        asiaMarketSection.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  // Modal content based on type
  const getModalTitle = () => {
    switch (modalType) {
      case 'estimate': return 'Campaign Estimator';
      case 'proposal': return 'Get Campaign Proposal';
      case 'consultation': return 'Schedule Consultation';
      case 'brand': return 'Campaign Case Study';
      default: return '';
    }
  };

  const getModalIcon = () => {
    switch (modalType) {
      case 'estimate': return <Calculator className="w-8 h-8 text-blue-600" />;
      case 'proposal': return <FileText className="w-8 h-8 text-blue-600" />;
      case 'consultation': return <Calendar className="w-8 h-8 text-blue-600" />;
      case 'brand': return <i className="ri-bar-chart-line text-2xl text-blue-600"></i>;
      default: return null;
    }
  };

  if (step > 1 || submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white p-6">
        <header className="max-w-5xl mx-auto text-center mb-10">
          <button 
            onClick={closeModal}
            className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-4 cursor-pointer"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Portal
          </button>
          <h1 className="text-3xl font-bold mb-2">DHB Group Advertising Portal</h1>
          <p className="text-gray-600 text-lg">Plan and estimate your next OOH/DOOH campaign across GCC and Asia.</p>
        </header>

        {!submitted ? (
          <div className="max-w-4xl mx-auto bg-white border rounded-3xl shadow-lg p-8">
            {step === 2 && modalType === 'estimate' && (
              <div className="text-center space-y-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                  <Calculator className="w-8 h-8 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold">Campaign Estimate</h2>
                <div className="bg-gray-50 rounded-2xl p-6 max-w-md mx-auto">
                  <div className="space-y-3 text-left">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Ad Format:</span>
                      <span className="font-semibold">{form.adType}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Region:</span>
                      <span className="font-semibold">{form.region}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Duration:</span>
                      <span className="font-semibold">{form.duration} month{form.duration > 1 ? 's' : ''}</span>
                    </div>
                    <hr className="my-3" />
                    <div className="flex justify-between text-lg font-bold">
                      <span>Estimated Cost:</span>
                      <span className="text-blue-600">${form.estimatedCost.toLocaleString()}</span>
                    </div>
                  </div>
                </div>
                <p className="text-gray-600 text-sm max-w-md mx-auto">
                  This is a preliminary estimate. Final pricing may vary based on specific requirements, availability, and market conditions.
                </p>
                <button 
                  onClick={handleConfirm}
                  className="bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium whitespace-nowrap cursor-pointer"
                >
                  Submit Consultation Request
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="max-w-2xl mx-auto bg-white border rounded-3xl shadow-lg p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Send className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold mb-4">
              {modalType === 'consultation' ? 'Consultation Scheduled!' : 'Request Submitted!'}
            </h2>
            <p className="text-gray-600 mb-6">
              {modalType === 'consultation' 
                ? 'Thank you for scheduling a consultation with DHB Group. Our team will contact you to confirm your appointment details.'
                : 'Thank you for your interest in DHB Group\'s advertising solutions. Our team will review your requirements and contact you within 24 hours.'
              }
            </p>
            <div className="bg-blue-50 rounded-xl p-4 mb-6">
              <h3 className="font-semibold text-blue-900 mb-2">Next Steps:</h3>
              <ul className="text-sm text-blue-800 space-y-1 text-left">
                {modalType === 'consultation' ? (
                  <>
                    <li>• Confirmation call within 2 hours</li>
                    <li>• Consultation meeting as scheduled</li>
                    <li>• Detailed proposal within 24 hours after meeting</li>
                    <li>• Campaign planning and execution timeline</li>
                  </>
                ) : (
                  <>
                    <li>• Initial consultation call within 24 hours</li>
                    <li>• Detailed proposal delivery within 3-5 business days</li>
                    <li>• Site visit and campaign planning (if approved)</li>
                    <li>• Campaign launch within 2-3 weeks</li>
                  </>
                )}
              </ul>
            </div>
            <button 
              onClick={() => {
                closeModal();
                resetForm();
              }}
              className="bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium whitespace-nowrap cursor-pointer"
            >
              Return to Portal
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Hero Slider */}
      <div className="flex items-center justify-center p-4 sm:p-6">
        <div className="w-full max-w-7xl h-[50vh] sm:h-[60vh] lg:h-[70vh] min-h-[320px] sm:min-h-[420px] rounded-2xl overflow-hidden relative shadow-2xl">
          {/* Slides */}
          {slides.map((slide, index) => (
            <div
              key={index}
              className={`absolute inset-0 grid grid-cols-1 lg:grid-cols-[1fr_560px] gap-6 lg:gap-10 items-center p-6 sm:p-8 lg:p-12 transition-all duration-800 ease-in-out bg-gradient-to-br ${slide.bgGradient} ${
                index === currentSlide 
                  ? 'opacity-100 scale-100 translate-y-0 pointer-events-auto' 
                  : 'opacity-0 scale-95 translate-y-2 pointer-events-none'
              }`}
            >
              {/* Content */}
              <div className="text-white max-w-2xl">
                <div className="inline-block px-3 py-1.5 sm:py-2 rounded-full bg-white/10 backdrop-blur-sm font-semibold text-xs sm:text-sm tracking-wider mb-3 sm:mb-4">
                  {slide.brand}
                </div>
                <h1 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold leading-tight mb-2 sm:mb-3 drop-shadow-lg">
                  {slide.title}
                </h1>
                <h2 className="text-sm sm:text-base lg:text-lg text-white/90 mb-3 sm:mb-4 font-medium">
                  {slide.subtitle}
                </h2>
                <p className="text-sm sm:text-base text-white/85 mb-4 sm:mb-6 leading-relaxed">
                  {slide.description}
                </p>
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                  <button 
                    onClick={() => openModal('estimate')}
                    className="bg-white text-slate-900 px-5 sm:px-6 py-2.5 sm:py-3 rounded-xl font-bold shadow-lg hover:shadow-xl transition-colors whitespace-nowrap cursor-pointer text-sm sm:text-base"
                  >
                    {slide.cta}
                  </button>
                  <button 
                    onClick={handleExploreGCCMarkets}
                    className="bg-transparent text-white/95 border border-white/20 px-4 sm:px-5 py-2.5 sm:py-3 rounded-xl font-semibold hover:bg-white/10 transition-colors whitespace-nowrap cursor-pointer text-sm sm:text-base"
                  >
                    {slide.ctaSecondary}
                  </button>
                </div>
              </div>

              {/* Visual - Hidden on mobile */}
              <div className="relative h-full rounded-xl overflow-hidden hidden lg:flex items-center justify-center p-5 shadow-inner">
                <div className="w-full h-full max-w-[520px] max-h-[400px] rounded-2xl relative overflow-hidden">
                  {/* Animated Shapes */}
                  {slide.shapes.map((shape, shapeIndex) => (
                    <div
                      key={shapeIndex}
                      className={`absolute opacity-75 mix-blend-screen filter blur-md ${
                        shape.type === 's1' 
                          ? 'w-80 h-80 rounded-full -left-10 -top-15 animate-pulse' 
                          : shape.type === 's2'
                          ? 'w-64 h-64 rounded-3xl -right-8 -top-8 animate-bounce'
                          : 'w-44 h-96 right-5 -bottom-10 rounded-2xl animate-pulse'
                      } bg-gradient-to-br ${shape.color}`}
                      style={{
                        animationDuration: shape.type === 's1' ? '10s' : shape.type === 's2' ? '11s' : '12s',
                        animationDelay: `${shapeIndex * 0.5}s`
                      }}
                    />
                  ))}
                </div>
              </div>

              {/* Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/5 to-white/5 pointer-events-none" />
            </div>
          ))}

          {/* Navigation */}
          <div className="absolute bottom-4 sm:bottom-8 left-1/2 transform -translate-x-1/2 flex items-center gap-2 sm:gap-4">
            <button 
              onClick={prevSlide}
              className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/30 transition-colors cursor-pointer"
            >
              <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
            
            <div className="flex gap-1.5 sm:gap-2">
              {slides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`w-2 h-2 sm:w-3 sm:h-3 rounded-full transition-colors cursor-pointer ${
                    index === currentSlide ? 'bg-white' : 'bg-white/50'
                  }`}
                />
              ))}
            </div>
            
            <button 
              onClick={nextSlide}
              className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/30 transition-colors cursor-pointer"
            >
              <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
          </div>

          {/* Quick Actions - Hidden on mobile */}
          <div className="absolute top-4 sm:top-6 right-4 sm:right-6 hidden md:flex gap-3">
            <button 
              onClick={() => openModal('estimate')}
              className="bg-white/10 backdrop-blur-sm text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-white/20 transition-colors font-medium whitespace-nowrap cursor-pointer flex items-center gap-2 text-sm"
            >
              <Calculator className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Quick Estimate</span>
            </button>
          </div>

          {/* Bottom Action Buttons */}
          <div className="absolute bottom-16 sm:bottom-20 lg:bottom-8 right-4 sm:right-8 flex flex-col sm:flex-row gap-2 sm:gap-3 w-[calc(100%-2rem)] sm:w-auto">
            <button 
              onClick={() => openModal('proposal')}
              className="bg-blue-600 text-white px-4 sm:px-6 lg:px-8 py-2.5 sm:py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium whitespace-nowrap cursor-pointer text-xs sm:text-sm lg:text-base text-center"
            >
              Get Campaign Proposal
            </button>
            <button 
              onClick={() => openModal('consultation')}
              className="bg-blue-600 text-white px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium whitespace-nowrap cursor-pointer text-xs sm:text-sm lg:text-base text-center"
            >
              Schedule Free Consultation
            </button>
          </div>
        </div>
      </div>

      {/* Social Media Section */}
      <section className="relative py-16 sm:py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3 sm:mb-4">Follow Our Global Channels</h2>
          <p className="text-base sm:text-lg text-gray-600 mb-8 sm:mb-12 max-w-2xl mx-auto">
            Stay connected with our latest news, innovations, and partnerships.
          </p>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:flex lg:flex-wrap lg:justify-center gap-4 sm:gap-6">
            {socialPlatforms.map((platform) => (
              <a
                key={platform.name}
                href={platform.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex flex-col items-center p-4 sm:p-6 bg-gray-50 rounded-xl hover:bg-blue-50 transition-all duration-300 hover:shadow-lg hover:-translate-y-1 cursor-pointer"
                aria-label={platform.alt}
              >
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-white rounded-full flex items-center justify-center mb-2 sm:mb-3 group-hover:bg-blue-600 transition-colors shadow-sm">
                  <i className={`${platform.icon} text-xl sm:text-2xl text-gray-700 group-hover:text-white transition-colors`}></i>
                </div>
                <span className="text-xs sm:text-sm font-medium text-gray-700 group-hover:text-blue-600 transition-colors">
                  {platform.name}
                </span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* 3D Immersive Advertising Section */}
      <section className="relative py-16 sm:py-20 lg:py-24 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800 overflow-hidden">
        {/* Background Video Placeholder */}
        <div className="absolute inset-0 opacity-20">
          <div className="w-full h-full bg-gradient-to-r from-blue-600/30 to-purple-600/30 animate-pulse"></div>
        </div>
        
        {/* Parallax Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-10 sm:top-20 left-5 sm:left-10 w-20 h-20 sm:w-32 sm:h-32 bg-gradient-to-br from-gold-400 to-yellow-500 rounded-full opacity-20 animate-pulse"></div>
          <div className="absolute bottom-10 sm:bottom-20 right-5 sm:right-10 w-32 h-32 sm:w-48 sm:h-48 bg-gradient-to-br from-silver-400 to-gray-300 rounded-full opacity-15 animate-bounce" style={{ animationDuration: '3s' }}></div>
          <div className="absolute top-1/2 left-1/4 w-16 h-48 sm:w-24 sm:h-96 bg-gradient-to-b from-blue-400 to-purple-600 opacity-10 transform rotate-12 animate-pulse" style={{ animationDuration: '4s' }}></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center">
            <div className="text-white space-y-4 sm:space-y-6 lg:space-y-8">
              <div className="space-y-3 sm:space-y-4">
                <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold leading-tight">
                  High-Impact 3D Anamorphic Advertising
                </h2>
                <h3 className="text-base sm:text-lg lg:text-xl text-blue-200 font-medium">
                  Immersive Visual Experiences for Global Brands
                </h3>
                <h4 className="text-sm sm:text-base lg:text-lg text-gray-200">
                  Next-Generation Outdoor & Digital OOH Solutions
                </h4>
              </div>
              
              <p className="text-sm sm:text-base lg:text-lg text-gray-200 leading-relaxed">
                We deliver cutting-edge 3D advertising experiences using ultra-high-resolution LED screens and advanced anamorphic design techniques. Our team produces immersive campaigns that elevate brand visibility and generate viral global exposure.
              </p>

              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <button 
                  onClick={() => openModal('estimate')}
                  className="bg-gradient-to-r from-gold-500 to-yellow-600 text-black px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-bold hover:shadow-2xl transition-all transform hover:scale-105 whitespace-nowrap cursor-pointer text-sm sm:text-base"
                >
                  Get 3D Campaign Quote
                </button>
                <button 
                  onClick={() => openModal('consultation')}
                  className="border-2 border-white/30 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-semibold hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer backdrop-blur-sm text-sm sm:text-base"
                >
                  View 3D Portfolio
                </button>
              </div>

              <div className="flex items-center gap-4 sm:gap-6 pt-2 sm:pt-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/10 rounded-full flex items-center justify-center backdrop-blur-sm">
                  <i className="ri-3d-view-line text-lg sm:text-xl text-gold-400"></i>
                </div>
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/10 rounded-full flex items-center justify-center backdrop-blur-sm">
                  <i className="ri-building-line text-lg sm:text-xl text-silver-400"></i>
                </div>
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/10 rounded-full flex items-center justify-center backdrop-blur-sm">
                  <i className="ri-global-line text-lg sm:text-xl text-blue-400"></i>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="aspect-video bg-gradient-to-br from-blue-600/20 to-purple-600/20 rounded-2xl backdrop-blur-sm border border-white/10 overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Spectacular%203D%20anamorphic%20LED%20billboard%20display%20showing%20floating%20holographic%20product%20in%20urban%20cityscape%2C%20massive%20curved%20digital%20screen%20with%20realistic%20depth%20illusion%2C%20crowds%20watching%20immersive%20advertising%20technology%2C%20futuristic%20DOOH%20installation%20with%20cinematic%20lighting%20and%20modern%20architecture&width=800&height=450&seq=3d-anamorphic-billboard-2024&orientation=landscape"
                  alt="3D Anamorphic Advertising Display"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
              </div>
              
              {/* Floating Stats */}
              <div className="absolute -bottom-4 sm:-bottom-6 -left-3 sm:-left-6 bg-white/10 backdrop-blur-md rounded-xl p-3 sm:p-4 border border-white/20">
                <div className="text-lg sm:text-2xl font-bold text-white">+340%</div>
                <div className="text-xs sm:text-sm text-gray-300">Engagement Rate</div>
              </div>
              
              <div className="absolute -top-4 sm:-top-6 -right-3 sm:-right-6 bg-white/10 backdrop-blur-md rounded-xl p-3 sm:p-4 border border-white/20">
                <div className="text-lg sm:text-2xl font-bold text-white">50M+</div>
                <div className="text-xs sm:text-sm text-gray-300">Daily Impressions</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Private Jet VVIP Advertising Section */}
      <section className="relative py-16 sm:py-20 lg:py-24 bg-gradient-to-br from-slate-800 via-gray-900 to-black overflow-hidden">
        {/* Glass Effect Background */}
        <div className="absolute inset-0 bg-gradient-to-r from-white/5 to-transparent backdrop-blur-3xl"></div>
        
        {/* Luxury Icons */}
        <div className="absolute top-10 sm:top-20 right-10 sm:right-20 w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-gold-400 to-yellow-600 rounded-full opacity-20 animate-pulse flex items-center justify-center">
          <i className="ri-vip-crown-line text-lg sm:text-xl text-white"></i>
        </div>
        <div className="absolute bottom-10 sm:bottom-20 left-10 sm:left-20 w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-silver-300 to-gray-400 rounded-full opacity-15 animate-bounce flex items-center justify-center" style={{ animationDuration: '4s' }}>
          <i className="ri-plane-line text-lg sm:text-xl text-white"></i>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center">
            <div className="relative order-2 lg:order-1">
              <div className="aspect-[4/3] bg-gradient-to-br from-gold-600/20 to-silver-600/20 rounded-2xl backdrop-blur-sm border border-white/10 overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Luxurious%20private%20jet%20interior%20with%20premium%20advertising%20displays%2C%20elegant%20cabin%20with%20high-end%20brand%20placements%2C%20sophisticated%20VVIP%20lounge%20atmosphere%2C%20ultra-wealthy%20passengers%2C%20exclusive%20aviation%20marketing%20environment%20with%20gold%20and%20leather%20details%2C%20cinematic%20luxury%20travel%20experience&width=800&height=600&seq=private-jet-vvip-advertising-2024&orientation=landscape"
                  alt="Private Jet VVIP Advertising"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
              </div>
              
              {/* Floating Luxury Stats */}
              <div className="absolute -bottom-4 sm:-bottom-6 -right-3 sm:-right-6 bg-black/40 backdrop-blur-md rounded-xl p-3 sm:p-4 border border-gold-400/30">
                <div className="text-lg sm:text-2xl font-bold text-gold-400">$50M+</div>
                <div className="text-xs sm:text-sm text-gray-300">Avg. Net Worth</div>
              </div>
              
              <div className="absolute -top-4 sm:-top-6 -left-3 sm:-left-6 bg-black/40 backdrop-blur-md rounded-xl p-3 sm:p-4 border border-silver-400/30">
                <div className="text-base sm:text-2xl font-bold text-silver-400">Ultra-Elite</div>
                <div className="text-xs sm:text-sm text-gray-300">Audience Tier</div>
              </div>
            </div>

            <div className="text-white space-y-4 sm:space-y-6 lg:space-y-8 order-1 lg:order-2">
              <div className="space-y-3 sm:space-y-4">
                <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold leading-tight">
                  Exclusive Media Placement for VVIP Audiences
                </h2>
                <h3 className="text-base sm:text-lg lg:text-xl text-gold-200 font-medium">
                  Private Jet Cabin & Lounge Advertising
                </h3>
                <h4 className="text-sm sm:text-base lg:text-lg text-gray-300">
                  Ultra-Luxury Marketing Solutions
                </h4>
              </div>
              
              <p className="text-sm sm:text-base lg:text-lg text-gray-200 leading-relaxed">
                Our VVIP advertising solutions provide brands with unparalleled access to ultra-high-net-worth audiences inside private aircraft, exclusive terminals, and luxury lounges. We collaborate with elite aviation partners to deliver tailored marketing campaigns designed for maximum prestige and visibility.
              </p>

              <div className="grid grid-cols-2 gap-3 sm:gap-4 py-2 sm:py-4">
                <div className="bg-white/5 rounded-lg p-3 sm:p-4 backdrop-blur-sm border border-white/10">
                  <div className="text-xl sm:text-2xl font-bold text-gold-400 mb-1">500+</div>
                  <div className="text-xs sm:text-sm text-gray-300">Private Jets</div>
                </div>
                <div className="bg-white/5 rounded-lg p-3 sm:p-4 backdrop-blur-sm border border-white/10">
                  <div className="text-xl sm:text-2xl font-bold text-silver-400 mb-1">50+</div>
                  <div className="text-xs sm:text-sm text-gray-300">VIP Lounges</div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <button 
                  onClick={() => openModal('consultation')}
                  className="bg-gradient-to-r from-gold-500 to-yellow-600 text-black px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-bold hover:shadow-2xl transition-all transform hover:scale-105 whitespace-nowrap cursor-pointer text-sm sm:text-base"
                >
                  Book VVIP Consultation
                </button>

                <button 
                  onClick={() => window.REACT_APP_NAVIGATE('/elite-portfolio?type=elite')}
                  className="border-2 border-gold-400/50 text-gold-400 px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-semibold hover:bg-gold-400/10 transition-all whitespace-nowrap cursor-pointer backdrop-blur-sm text-sm sm:text-base"
                >
                  View Elite Portfolio
                </button>

                <button 
                  onClick={() => window.REACT_APP_NAVIGATE('/elite-portfolio?type=brand')}
                  className="border-2 border-white/30 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-semibold hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer backdrop-blur-sm text-sm sm:text-base"
                >
                  View Brand Portfolio
                </button>
              </div>

              <div className="flex items-center gap-4 sm:gap-6 pt-2 sm:pt-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-gold-500/20 to-yellow-600/20 rounded-full flex items-center justify-center backdrop-blur-sm border border-gold-400/30">
                  <i className="ri-vip-crown-line text-lg sm:text-xl text-gold-400"></i>
                </div>
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-silver-400/20 to-gray-500/20 rounded-full flex items-center justify-center backdrop-blur-sm border border-silver-400/30">
                  <i className="ri-plane-line text-lg sm:text-xl text-silver-400"></i>
                </div>
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-blue-500/20 to-purple-600/20 rounded-full flex items-center justify-center backdrop-blur-sm border border-blue-400/30">
                  <i className="ri-award-line text-lg sm:text-xl text-blue-400"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Brand Portfolio Section */}
      <section id="brand-portfolio-section" className="py-16 sm:py-20 lg:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-light text-gray-900 mb-3 sm:mb-4">
              Global Brand Portfolio
            </h2>
            <p className="text-base sm:text-lg lg:text-xl text-gray-600 font-light max-w-3xl mx-auto">
              Trusted by leading international brands across technology, luxury, automotive, and lifestyle sectors.
            </p>
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 sm:gap-6">
            {brandCases.map((brand, index) => (
              <div 
                key={index}
                onClick={() => handleBrandClick(brand)}
                className="bg-white rounded-xl p-4 sm:p-6 shadow-sm hover:shadow-md transition-all hover:-translate-y-1 cursor-pointer"
              >
                <div className="aspect-video bg-gray-50 rounded-lg mb-3 sm:mb-4 flex items-center justify-center overflow-hidden">
                  <img 
                    src={brand.image}
                    alt={`${brand.name} logo`}
                    className="w-full h-full object-contain p-2 sm:p-3"
                  />
                </div>
                <h4 className="text-xs sm:text-sm lg:text-base font-medium text-gray-900 mb-1 sm:mb-2">
                  {brand.name}
                </h4>
                <p className="text-xs text-blue-600 font-medium mb-1 sm:mb-2">
                  {brand.category}
                </p>
                <p className="text-xs text-gray-600 font-light leading-relaxed">
                  Global brand promotion
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Modal */}
      {modalType !== 'none' && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-6 z-50">
          <div className="bg-white rounded-2xl p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center gap-3">
                {getModalIcon()}
                <h2 className="text-2xl font-bold">{getModalTitle()}</h2>
              </div>
              <button 
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600 cursor-pointer text-xl"
              >
                ✕
              </button>
            </div>
            
            {modalType === 'brand' && selectedBrand && (
              <div className="space-y-6">
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <img
                      src={selectedBrand.campaign.campaignImage}
                      alt={selectedBrand.campaign.title}
                      className="w-full h-64 object-cover rounded-lg"
                    />
                  </div>
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">
                        {selectedBrand.campaign.title}
                      </h3>
                      <div className="space-y-2 text-sm text-gray-600">
                        <div className="flex justify-between">
                          <span>Location:</span>
                          <span className="font-medium">{selectedBrand.campaign.location}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Duration:</span>
                          <span className="font-medium">{selectedBrand.campaign.duration}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Format:</span>
                          <span className="font-medium">{selectedBrand.campaign.format}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-blue-50 rounded-lg p-4">
                      <h4 className="font-semibold text-blue-900 mb-3">Campaign Results</h4>
                      <div className="grid grid-cols-3 gap-4 text-center">
                        <div>
                          <div className="text-lg font-bold text-blue-600">
                            {selectedBrand.campaign.results.brandAwareness}
                          </div>
                          <div className="text-xs text-blue-800">Brand Awareness</div>
                        </div>
                        <div>
                          <div className="text-lg font-bold text-blue-600">
                            {selectedBrand.campaign.results.salesIncrease}
                          </div>
                          <div className="text-xs text-blue-800">Sales Increase</div>
                        </div>
                        <div>
                          <div className="text-lg font-bold text-blue-600">
                            {selectedBrand.campaign.results.engagement}
                          </div>
                          <div className="text-xs text-blue-800">Engagement</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Campaign Overview</h4>
                  <p className="text-gray-600 leading-relaxed">
                    {selectedBrand.campaign.description}
                  </p>
                </div>
                
                <div className="flex gap-4 pt-4">
                  <button 
                    onClick={() => {
                      closeModal();
                      openModal('consultation');
                    }}
                    className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium whitespace-nowrap cursor-pointer"
                  >
                    Schedule Free Consultation
                  </button>
                  <button 
                    onClick={closeModal}
                    className="border border-gray-300 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-50 transition-colors font-medium whitespace-nowrap cursor-pointer"
                  >
                    Close
                  </button>
                </div>
              </div>
            )}

            {modalType !== 'brand' && (
              <form onSubmit={handleSubmit} className="space-y-6" data-readdy-form>
                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label className="font-semibold text-sm block mb-2">Company Name *</label>
                    <input 
                      name="company"
                      className="w-full border rounded-xl p-3 text-sm" 
                      value={form.company} 
                      onChange={(e) => setForm({ ...form, company: e.target.value })} 
                      required 
                    />
                  </div>
                  <div>
                    <label className="font-semibold text-sm block mb-2">Contact Person *</label>
                    <input 
                      name="contact"
                      className="w-full border rounded-xl p-3 text-sm" 
                      value={form.contact} 
                      onChange={(e) => setForm({ ...form, contact: e.target.value })} 
                      required 
                    />
                  </div>
                  <div>
                    <label className="font-semibold text-sm block mb-2">Email *</label>
                    <input 
                      type="email" 
                      name="email"
                      className="w-full border rounded-xl p-3 text-sm" 
                      value={form.email} 
                      onChange={(e) => setForm({ ...form, email: e.target.value })} 
                      required 
                    />
                  </div>
                  <div>
                    <label className="font-semibold text-sm block mb-2">Phone</label>
                    <input 
                      name="phone"
                      className="w-full border rounded-xl p-3 text-sm" 
                      value={form.phone} 
                      onChange={(e) => setForm({ ...form, phone: e.target.value })} 
                    />
                  </div>
                </div>

                {modalType !== 'consultation' && (
                  <div>
                    <label className="font-semibold text-sm block mb-3">Choose Ad Format</label>
                    <div className="grid md:grid-cols-2 gap-3">
                      {adOptions.map((ad) => (
                        <button
                          key={ad.name}
                          type="button"
                          onClick={() => setForm({ ...form, adType: ad.name })}
                          className={`p-4 rounded-xl border text-left hover:shadow-md transition cursor-pointer ${
                            form.adType === ad.name ? "bg-blue-600 text-white" : "bg-gray-50 hover:bg-gray-100"
                          }`}
                        >
                          <h4 className="font-semibold text-sm">{ad.name}</h4>
                          <p className="text-xs mt-1 opacity-80">{ad.desc}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {modalType === 'consultation' ? (
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="font-semibold text-sm block mb-2">Preferred Date</label>
                      <input 
                        type="date" 
                        name="preferredDate"
                        className="w-full border rounded-xl p-3 text-sm" 
                        value={form.preferredDate} 
                        onChange={(e) => setForm({ ...form, preferredDate: e.target.value })} 
                        min={new Date().toISOString().split('T')[0]}
                      />
                    </div>
                    <div>
                      <label className="font-semibold text-sm block mb-2">Preferred Time</label>
                      <select 
                        name="preferredTime"
                        className="w-full border rounded-xl p-3 text-sm pr-8" 
                        value={form.preferredTime} 
                        onChange={(e) => setForm({ ...form, preferredTime: e.target.value })}
                      >
                        <option value="">Select time</option>
                        <option value="09:00">09:00 AM</option>
                        <option value="10:00">10:00 AM</option>
                        <option value="11:00">11:00 AM</option>
                        <option value="14:00">02:00 PM</option>
                        <option value="15:00">03:00 PM</option>
                        <option value="16:00">04:00 PM</option>
                      </select>
                    </div>
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="font-semibold text-sm block mb-2">Duration (months)</label>
                      <input 
                        type="number" 
                        min={1} 
                        name="duration"
                        className="w-full border rounded-xl p-3 text-sm" 
                        value={form.duration} 
                        onChange={(e) => setForm({ ...form, duration: Number(e.target.value) })} 
                      />
                    </div>
                    <div>
                      <label className="font-semibold text-sm block mb-2">Target Region</label>
                      <select 
                        name="region"
                        className="w-full border rounded-xl p-3 text-sm pr-8" 
                        value={form.region} 
                        onChange={(e) => setForm({ ...form, region: e.target.value })}
                      >
                        <option value="UAE">UAE</option>
                        <option value="Saudi">Saudi Arabia</option>
                        <option value="Qatar">Qatar</option>
                        <option value="Korea">South Korea</option>
                        <option value="Japan">Japan</option>
                        <option value="Singapore">Singapore</option>
                      </select>
                    </div>
                  </div>
                )}

                <div>
                  <label className="font-semibold text-sm block mb-2">
                    {modalType === 'consultation' ? 'Meeting Agenda' : 'Additional Requirements'}
                  </label>
                  <textarea 
                    name="message"
                    className="w-full border rounded-xl p-3 text-sm h-24 resize-none" 
                    placeholder={modalType === 'consultation' 
                      ? 'What would you like to discuss during the consultation?'
                      : 'Any specific requirements or questions about your campaign?'
                    }
                    value={form.message} 
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    maxLength={500}
                  />
                  <div className="text-xs text-gray-500 mt-1">{form.message.length}/500 characters</div>
                </div>

                <button 
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-blue-600 text-white py-3 rounded-xl font-semibold hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Submitting...' : 
                   modalType === 'estimate' ? 'Calculate Estimate' : 
                   modalType === 'proposal' ? 'Request Proposal' : 
                   'Schedule Consultation'}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdPortalPage;
